import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BoltOutlined,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileDownload,
  FileUpload,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

import {
  IPmAdminFilter,
  IPmAdminRow,
  useAdminPmDelete,
  useAdminPmExport,
  useAdminPmImport,
  useAdminPmList,
  useAdminPmTrigger,
} from '../../hooks/reactQuerys/ProcessManagerAdminQueries';
import { useGetProcessManagerTypes } from '../../hooks/reactQuerys/ProcessManagerQueries';
import { useAlert } from '../../context/AlertContext';
import { PmAdminEditDialog } from './PmAdminEditDialog';
import { PmTypeAdminSection } from './PmTypeAdminSection';

// Stammdaten aus ``ProcessManagerState`` (DB):
//   1 = Offen        — Tracker läuft im Sweep mit
//   2 = Übermittelt  — Aktion ausgeführt, Bestätigung erwartet
//   3 = Eingetragen  — abgeschlossen (Ende-Kriterium erreicht)
const STATE_LABELS: Record<number, string> = {
  0: 'Neu',
  1: 'Offen',
  2: 'Übermittelt',
  3: 'Eingetragen',
};

const STATE_COLORS: Record<
  number,
  'default' | 'success' | 'warning' | 'info' | 'error'
> = {
  0: 'info',
  1: 'success',
  2: 'warning',
  3: 'default',
};

const formatDate = (val?: string | null): string => {
  if (!val) return '–';
  try {
    return new Date(val).toLocaleString('de-DE');
  } catch {
    return val;
  }
};

/**
 * Dev-Admin-Übersicht für ProcessManager-Einträge. Erlaubt:
 *   - Filter nach Type/State/Vorgang/Substring in ``data``
 *   - Paginierung (server-side, default 25/Seite, max 200)
 *   - Trigger einzelner Einträge (Sweep on-demand)
 *   - Edit (data, state, Tries, …) inkl. Automationen
 *   - Hard-Delete
 *   - Bulk-Export der gefilterten Liste + Bulk-Import via JSON
 *
 * Nur für interne Entwickler-/Support-Zugriffe — die Page hängt unter
 * dem versteckten DevHub.
 */
export const ProcessManagerAdminOverview: React.FC = () => {
  const { showAlert } = useAlert();

  const [filterType, setFilterType] = useState<number | ''>('');
  const [filterState, setFilterState] = useState<number | ''>(1);
  const [filterLT, setFilterLT] = useState<string>('');
  const [filterQ, setFilterQ] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const filter: IPmAdminFilter = useMemo(
    () => ({
      page,
      pageSize,
      idProcessManagerType: filterType === '' ? null : Number(filterType),
      idProcessManagerState: filterState === '' ? null : Number(filterState),
      idLegalTransaction:
        filterLT.trim() === '' ? null : Number(filterLT.trim()) || null,
      q: filterQ,
    }),
    [page, pageSize, filterType, filterState, filterLT, filterQ]
  );

  const { pmListRows, pmListTotal, isFetchingPmList, refetchPmList } =
    useAdminPmList(filter);

  const { processManagerTypes } = useGetProcessManagerTypes();
  const { triggerPm, isTriggeringPm } = useAdminPmTrigger();
  const { deletePm, isDeletingPm } = useAdminPmDelete();
  const { exportPm, isExportingPm } = useAdminPmExport();
  const { importPm, isImportingPm } = useAdminPmImport();

  const [editPm, setEditPm] = useState<IPmAdminRow | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleTrigger = async (idPm: number) => {
    try {
      const res = await triggerPm(idPm);
      const after = res.after;
      showAlert({
        text: after
          ? `PM ${idPm} gesweept — neuer State ${after.idProcessManagerState}, Tries ${after.Tries ?? 0}`
          : `PM ${idPm} gesweept`,
        severity: 'success',
      });
    } catch (e) {
      showAlert({
        text:
          e instanceof Error
            ? `Trigger fehlgeschlagen: ${e.message}`
            : 'Trigger fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  const handleDelete = async (idPm: number) => {
    if (
      !window.confirm(
        `PM ${idPm} dauerhaft aus der Datenbank löschen? Dies entfernt auch alle zugehörigen Automationen. Die Aktion ist nicht rückgängig zu machen.`
      )
    )
      return;
    try {
      await deletePm(idPm);
      showAlert({ text: `PM ${idPm} gelöscht.`, severity: 'success' });
    } catch (e) {
      showAlert({
        text:
          e instanceof Error
            ? `Löschen fehlgeschlagen: ${e.message}`
            : 'Löschen fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  const handleExport = async () => {
    try {
      const res = await exportPm(filter);
      const blob = new Blob([JSON.stringify(res, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pm-export-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showAlert({
        text: `${res.count} PMs exportiert.`,
        severity: 'success',
      });
    } catch (e) {
      showAlert({
        text:
          e instanceof Error
            ? `Export fehlgeschlagen: ${e.message}`
            : 'Export fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  const handleImportFile = async (file: File) => {
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const rows = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.rows)
          ? parsed.rows
          : null;
      if (!rows) {
        throw new Error('JSON enthält weder Liste noch ``rows``-Array.');
      }
      const result = await importPm({ rows });
      showAlert({
        text: `Import: ${result.inserted} eingefügt, ${result.updated} aktualisiert, ${result.skipped} übersprungen.`,
        severity: 'success',
      });
    } catch (e) {
      showAlert({
        text:
          e instanceof Error
            ? `Import fehlgeschlagen: ${e.message}`
            : 'Import fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction='row'
        spacing={2}
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 2 }}
      >
        <Typography variant='h4'>ProcessManager (Dev-Admin)</Typography>
        <Stack direction='row' spacing={1}>
          <Button
            variant='outlined'
            startIcon={<RefreshIcon />}
            onClick={() => refetchPmList()}
            disabled={isFetchingPmList}
          >
            Neu laden
          </Button>
          <Button
            variant='outlined'
            startIcon={<FileDownload />}
            onClick={handleExport}
            disabled={isExportingPm || pmListTotal === 0}
          >
            Export
          </Button>
          <input
            type='file'
            accept='application/json'
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) {
                void handleImportFile(f);
                e.target.value = '';
              }
            }}
          />
          <Button
            variant='outlined'
            startIcon={<FileUpload />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isImportingPm}
          >
            Import
          </Button>
        </Stack>
      </Stack>

      <Alert severity='info' sx={{ mb: 2 }}>
        Diese View greift roh auf die ProcessManager-Tabelle zu — Edits sind
        direkt persistiert und nicht zurückrollbar. Vor Bulk-Imports einen
        Export als Backup machen.
      </Alert>

      <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
        <Stack direction='row' spacing={2} flexWrap='wrap'>
          <TextField
            select
            size='small'
            label='Typ'
            value={filterType}
            onChange={e => {
              setFilterType(
                e.target.value === '' ? '' : Number(e.target.value)
              );
              setPage(1);
            }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value=''>– alle Typen –</MenuItem>
            {(processManagerTypes ?? []).map(t => (
              <MenuItem
                key={`pmt-${t.idProcessManagerType}`}
                value={t.idProcessManagerType}
              >
                {t.idProcessManagerType} – {t.ProcessManagerType}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size='small'
            label='Status'
            value={filterState}
            onChange={e => {
              setFilterState(
                e.target.value === '' ? '' : Number(e.target.value)
              );
              setPage(1);
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value=''>– alle Status –</MenuItem>
            {[0, 1, 2, 3].map(s => (
              <MenuItem key={`pms-${s}`} value={s}>
                {s} – {STATE_LABELS[s]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size='small'
            label='idLegalTransaction'
            value={filterLT}
            onChange={e => {
              setFilterLT(e.target.value);
              setPage(1);
            }}
            inputProps={{ inputMode: 'numeric' }}
            sx={{ minWidth: 180 }}
          />
          <TextField
            size='small'
            label='Suche in data (LIKE %q%)'
            value={filterQ}
            onChange={e => {
              setFilterQ(e.target.value);
              setPage(1);
            }}
            sx={{ flex: 1, minWidth: 220 }}
          />
        </Stack>
      </Paper>

      <TableContainer component={Paper} variant='outlined'>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={80}>ID</TableCell>
              <TableCell width={200}>Typ</TableCell>
              <TableCell width={110}>Status</TableCell>
              <TableCell width={100}>Vorgang</TableCell>
              <TableCell width={120}>DateToCheck</TableCell>
              <TableCell width={160}>Letzte Prüfung</TableCell>
              <TableCell width={60}>Tries</TableCell>
              <TableCell width={60}>Exit</TableCell>
              <TableCell>data (Auszug)</TableCell>
              <TableCell width={70}>Auto</TableCell>
              <TableCell width={160} align='right'>
                Aktionen
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetchingPmList && pmListRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align='center'>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}
            {!isFetchingPmList && pmListRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align='center'>
                  <em>Keine Einträge mit den aktuellen Filtern.</em>
                </TableCell>
              </TableRow>
            )}
            {pmListRows.map(row => (
              <TableRow key={`pm-${row.idProcessManager}`} hover>
                <TableCell>
                  <Typography variant='body2' fontFamily='monospace'>
                    {row.idProcessManager}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Stack direction='row' spacing={0.5} alignItems='center'>
                    <Chip
                      size='small'
                      variant='outlined'
                      label={row.idProcessManagerType}
                    />
                    <Typography variant='body2'>
                      {row.ProcessManagerType ?? '?'}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    size='small'
                    color={STATE_COLORS[row.idProcessManagerState] ?? 'default'}
                    label={`${row.idProcessManagerState} · ${
                      STATE_LABELS[row.idProcessManagerState] ?? '?'
                    }`}
                  />
                </TableCell>
                <TableCell>
                  {row.idLegalTransaction ? (
                    <Typography variant='body2' fontFamily='monospace'>
                      #{row.idLegalTransaction}
                    </Typography>
                  ) : (
                    '–'
                  )}
                </TableCell>
                <TableCell>{formatDate(row.DateToCheck)}</TableCell>
                <TableCell>{formatDate(row.LastTry_at)}</TableCell>
                <TableCell align='right'>{row.Tries ?? 0}</TableCell>
                <TableCell align='right'>
                  {row.ExitCodeLastTry ?? '–'}
                </TableCell>
                <TableCell sx={{ maxWidth: 320 }}>
                  <Tooltip title={row.data ?? '∅'}>
                    <Typography
                      variant='caption'
                      fontFamily='monospace'
                      sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.data ?? '∅'}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell align='right'>
                  <Chip size='small' label={row.Automations?.length ?? 0} />
                </TableCell>
                <TableCell align='right'>
                  <Stack
                    direction='row'
                    spacing={0.5}
                    justifyContent='flex-end'
                  >
                    <Tooltip title='Trigger (Sweep auslösen)'>
                      <span>
                        <IconButton
                          size='small'
                          color='primary'
                          onClick={() => handleTrigger(row.idProcessManager)}
                          disabled={isTriggeringPm}
                        >
                          <BoltOutlined fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title='Bearbeiten'>
                      <IconButton size='small' onClick={() => setEditPm(row)}>
                        <EditIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Löschen (hard, inkl. Automationen)'>
                      <span>
                        <IconButton
                          size='small'
                          color='error'
                          disabled={isDeletingPm}
                          onClick={() => handleDelete(row.idProcessManager)}
                        >
                          <DeleteIcon fontSize='small' />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component='div'
        count={pmListTotal}
        page={page - 1}
        onPageChange={(_e, newPage) => setPage(newPage + 1)}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[10, 25, 50, 100, 200]}
        onRowsPerPageChange={e => {
          setPageSize(parseInt(e.target.value, 10));
          setPage(1);
        }}
        labelRowsPerPage='Einträge pro Seite'
      />

      <PmAdminEditDialog
        open={editPm !== null}
        row={editPm}
        onClose={() => setEditPm(null)}
      />

      <PmTypeAdminSection />
    </Box>
  );
};
